/**
 * What a drag or a button press does to an order.
 *
 * Pure, and separate from the component on purpose. Dragging is hard to drive
 * in a test environment with no layout; deciding what a drop *means* is not,
 * and that is where the bugs are. The component's drag handler and its move
 * buttons both call these, so what the two do can never drift apart.
 *
 * The answer is a compacted list: `value[i]` is what is in position i+1, and
 * the positions after it are empty. There are no holes, so "position 3 is
 * filled and 1 and 2 are not" cannot be expressed — which is deliberate, since
 * a student reading "3. Water evaporates" above two blanks has no way to know
 * whether they have answered third or first.
 */
/** Put `optionId` at `index`, taking it out of wherever it was. */
export function placeAt(value, optionId, index) {
    const without = value.filter((id) => id !== optionId);
    // Clamped rather than padded: dropping on the fifth position of a list with
    // two things in it appends, because a gap is not something this can hold.
    const target = Math.max(0, Math.min(index, without.length));
    return [...without.slice(0, target), optionId, ...without.slice(target)];
}
/** Take `optionId` back out of the answer. Returning what was never there is a no-op. */
export function removeFrom(value, optionId) {
    return value.filter((id) => id !== optionId);
}
/** Move a placed option one position up (-1) or down (+1). At either end, nothing moves. */
export function moveBy(value, optionId, delta) {
    const from = value.indexOf(optionId);
    if (from === -1)
        return [...value];
    const to = from + delta;
    if (to < 0 || to >= value.length)
        return [...value];
    return placeAt(value, optionId, to);
}
/** The options not yet placed, in the order they were given. */
export function remaining(options, value) {
    const placed = new Set(value);
    return options.filter((option) => !placed.has(option.id));
}
/**
 * What a drop means.
 *
 * The one piece of drag behaviour worth testing, and the one a test
 * environment with no layout can still reach: the library decides *what* was
 * dropped *where*, and this decides what that does to the answer.
 */
export function applyDrop(value, optionId, target) {
    return target.kind === 'available'
        ? removeFrom(value, optionId)
        : placeAt(value, optionId, target.index);
}
//# sourceMappingURL=ordering.js.map